/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

type UseBorrowerData = {
  borrowerData: Record<string, BorrowerData>; // borrowerData['01'] returns the BorrowerData for the borrower with borrowerNumber '01'
  currentBorrower: BorrowerData | null;
  primaryCoBorrowers: BorrowerDatas;
  loadingBorrowerData: boolean;
  updateBorrowerData: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  reloadBorrowerData: () => void;
  fetchingBorrowerData: boolean;
  borrowerDataArr: Array<BorrowerData>;
};

const useBorrowerData = (): UseBorrowerData => {
  const { get } = useAbortController();
  const queryClient = useQueryClient();
  const { token, email, servicerUser } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const queryKey = useMemo(() => ['brwrData', 'list', loanNumber, token], [loanNumber, token]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!loanNumber || !token) return { data: [] };
      try {
        const request = {
          url: process.env.REACT_APP_BRWR_LIST.replace('?', loanNumber),
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useBorrowerData.response: `, response);
        return { data: response };
      } catch (error) {
        console.error(`useBorrowerData.caught: ${error.message}`);
        return { data: [] };
      }
    },
  });

  const borrowerData = useMemo(() => {
    if (isLoading) return {};
    if (!('data' in data) || !Array.isArray(data.data) || data.data.length === 0) return {};
    return data.data.reduce((map, borrower) => {
      map[borrower.borrowerNumber] = borrower;
      return map;
    }, {});
  }, [isLoading, data]);

  const currentBorrower = useMemo(() => {
    if (servicerUser) return null;
    if (isLoading) return null;
    if (!('data' in data) || !Array.isArray(data.data) || data.data.length === 0) return null;
    return data.data.find((borrower) => borrower.emailAddress === email) || null;
  }, [isLoading, data, email, servicerUser]);

  const primaryCoBorrowers = useMemo(() => {
    if (isLoading) return [];
    if (!('data' in data) || !Array.isArray(data.data) || data.data.length === 0) return [];
    return data.data.sort((a, b) => parseInt(a.borrowerNumber) - parseInt(b.borrowerNumber)).slice(0, 4);
  }, [isLoading, data]);

  const reloadBorrowerData = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  const updateBorrowerData = useCallback(
    async (fields: Partial<BorrowerData>, setErrors: Dispatch<SetStateAction<Record<string, string>>>) => {
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      const errors = {
        generic: '',
      };
      if (!fields.borrowerNumber) {
        errors.generic = 'Missing borrower number';
      }
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan/${loanNumber}/borrower/${fields.borrowerNumber}`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useBorrowerData.response: `, response);
      reloadBorrowerData();
      return response;
    },
    [loanNumber, get, reloadBorrowerData, token],
  );

  return {
    borrowerData,
    currentBorrower,
    primaryCoBorrowers,
    loadingBorrowerData: isLoading,
    fetchingBorrowerData: isFetching,
    updateBorrowerData,
    reloadBorrowerData,
    borrowerDataArr: data?.data ? data.data : null,
  };
};

export default useBorrowerData;
